import React from 'react';
import { HeroBanner, SlideContainer } from './slideStyles';
import { useSelector } from 'react-redux';
import { getSelectedAssessmentPeriodId, getLeadershipTeams, getSelectedAssessmentPeriod } from '@/common/selectors/auth';
import userContextSlice from 'common/slices/userContextSlice';
import { useGetCultureHubPillarsSummaryQuery } from '@/areas/culture-hub/api/cultureHubApi';
import { Check } from 'lucide-react';
import styled from 'styled-components';
import teamMeetingImage from 'assets/images/team_meeting.jpg';

// Override shared banner to allow flush image placement and scroll when content grows
const SlideBanner = styled(HeroBanner)`
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0 1.25rem 0rem;
  gap: 0;
  min-height: 0;
  max-height: 100%;

  @media (max-width: 600px) and (orientation: portrait) {
    padding: 0 0.35rem 0;
  }
`;

const HeroRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0;

  @media (max-width: 1180px) {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem 1.75rem 1.5rem;
  }

  @media (max-width: 600px) and (orientation: portrait) {
    padding: 0.8rem 0.65rem 1rem;
  }
`;

const HeroLeft = styled.div`
  flex: 1;
  min-width: 0;
  padding: 2.1rem 3.25rem 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (max-width: 1180px) {
    padding: 1.25rem 1.5rem 0rem;
  }

  @media (max-width: 600px) and (orientation: portrait) {
    padding: 0.75rem 0.35rem 0;
  }
`;

const HeroLabelEl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  color: #166534;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.5rem;

`;

const HeroLabelIconWrapper = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(16, 185, 129, 0.16);
  display: grid;
  place-items: center;
`;

const HeroLabelIcon = styled(Check)`
  width: 12px;
  height: 12px;
  color: #16a34a;
`;

const HeroHeadlineEl = styled.h3`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  color: #111827;
  line-height: 1.25;
  font-size: 24px;
`;

const HeroBodyEl = styled.p`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #475569;
  line-height: 1.65;
  padding-bottom: 0.5rem;

  @media (max-width: 1180px) {
    font-size: 13px;
  }

  @media (max-height: 650px) {
    font-size: 12px;
    line-height: 1.5;
  }
`;

const HeroBodySecondary = styled(HeroBodyEl)`
  @media (max-height: 650px) {
    display: none;
  }
`;

const HeroImageCol = styled.div`
  width: 40%;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  align-self: stretch;

  @media (max-width: 1180px) {
    display: none;
  }

  @media (max-height: 650px) {
    height: 180px;
    min-height: 180px;
  }
`;

const TeamImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ImageFadeOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(to right, white 0%, transparent 40%),
    linear-gradient(to top, white 0%, transparent 40%);
  pointer-events: none;
`;

const CardSection = styled.div`
  padding: 1.75rem 1.25rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;

  @media (max-height: 650px) {
    padding: 0rem 2.5rem 1rem;
  }

  @media (max-width: 600px) and (orientation: portrait) {
    padding: 1rem 0.35rem 0.85rem;
  }
`;

const CardGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const cardBackgroundByAccent = (accent: string) => {
  switch (accent) {
    case '#16a34a':
      return 'rgba(217, 249, 234, 0.35)';
    case '#2563eb':
      return 'rgba(219, 234, 254, 0.35)';
    case '#8b5cf6':
      return 'rgba(237, 233, 254, 0.35)';
    default:
      return '#ffffff';
  }
};

const ActionCard = styled.div<{ $accent: string }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 70px;
  max-height: 95px;
  padding: 1rem 1.1rem;
  border-radius: 1rem;
  background: ${props => cardBackgroundByAccent(props.$accent)};
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
  position: relative;
  margin-left: 1.5rem;
  margin-right: 1.5rem;

  @media (max-width: 1180px) {
    padding: 0.8rem 0.9rem;
    min-height: 72px;
    max-height: none;
    margin-left: 0;
    margin-right: 0;
  }

  @media (max-height: 650px) {
    padding: 0.7rem 0.8rem;
    min-height: 68px;
    max-height: none;
    gap: 0.75rem;
  }

  @media (max-width: 600px) and (orientation: portrait) {
    padding: 0.75rem 0.8rem;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 5px;
    height: 100%;
    background: ${props => props.$accent};
    border-top-left-radius: 1rem;
    border-bottom-left-radius: 1rem;
  }
`;

const CardIcon = styled.div<{ $bg: string; $color: string }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${props => props.$bg};
  color: ${props => props.$color};
  display: grid;
  align-items: start;
  justify-items: end;
  padding: 14px 10px 0 0;
  font-size: 24px;
  line-height: 1;
  text-align: right;
  flex-shrink: 0;

  @media (max-width: 1180px) {
    width: 48px;
    height: 48px;
    padding: 12px 8px 0 0;
    font-size: 20px;
  }

  @media (max-height: 650px) {
    width: 44px;
    height: 44px;
    padding: 10px 8px 0 0;
    font-size: 18px;
  }
`;

const CardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const CardTitle = styled.h4`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: #111827;

  @media (max-width: 1180px) {
    font-size: 16px;
  }

  @media (max-height: 650px) {
    font-size: 15px;
  }
`;

const CardDescription = styled.p`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  line-height: 1.7;
  color: #475569;

  @media (max-width: 1180px) {
    font-size: 12px;
  }

  @media (max-height: 650px) {
    font-size: 11px;
    line-height: 1.5;
  }
`;


const Slide1AssessmentComplete: React.FC = () => {
  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId) as number | null;
  const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod);
  const leadershipTeams = useSelector(getLeadershipTeams);
  const selectedTeamId = useSelector(
    userContextSlice.selectors.getSelectedTeamId as any
  ) as number | undefined;
  const teamId = selectedTeamId;
  const shouldFetchPillars = assessmentPeriodId != null && selectedTeamId != null;

  useGetCultureHubPillarsSummaryQuery(
    { assessmentPeriodId: assessmentPeriodId!, teamId },
    { skip: !shouldFetchPillars }
  );

  return (
    <SlideContainer>
      <SlideBanner>
        <HeroRow>
          <HeroLeft>
            <HeroLabelEl>
              <HeroLabelIconWrapper>
                <HeroLabelIcon />
              </HeroLabelIconWrapper>
              Assessment complete - { selectedAssessmentPeriod?.name || 'Current Assessment Period' }
            </HeroLabelEl>
            <HeroHeadlineEl>
              {'Your team\'s results are ready'}
            </HeroHeadlineEl>
            <HeroBodyEl>
              Your team confidentially shared how they experience your culture. We've analyzed their responses to highlight your team's strengths, identify opportunities for improvement, and recommend the actions likely to have the greatest impact.
            </HeroBodyEl>
            <HeroBodySecondary>
              Over the next few screens, we'll guide you through your team's results, highlight the insights that matter most, and recommend the next steps to help strengthen your team's culture.
            </HeroBodySecondary>
          </HeroLeft>
          <HeroImageCol>
            <TeamImage src={teamMeetingImage} alt="Team meeting" />
            <ImageFadeOverlay />
          </HeroImageCol>
        </HeroRow>

        <CardSection>
          <CardGrid>
            <ActionCard $accent="#16a34a">
              <CardIcon $bg="#dcfce7" $color="#16a34a">👥</CardIcon>
              <CardText>
                <CardTitle>What is culture?</CardTitle>
                <CardDescription>
                  Culture is your team's everyday experience—how supported, included, and empowered people feel to contribute and grow.
                </CardDescription>
              </CardText>
            </ActionCard>

            <ActionCard $accent="#2563eb">
              <CardIcon $bg="#dbeafe" $color="#2563eb">📊</CardIcon>
              <CardText>
                <CardTitle>What you'll learn</CardTitle>
                <CardDescription>
                  Discover your team's strengths, challenges, and key feedback themes. We'll explain what each culture pillar means and why it matters.
                </CardDescription>
              </CardText>
            </ActionCard>

            <ActionCard $accent="#8b5cf6">
              <CardIcon $bg="#ede9fe" $color="#8b5cf6">🎯</CardIcon>
              <CardText>
                <CardTitle>What happens next?</CardTitle>
                <CardDescription>
                  We'll offer recommendations you can add to your Focus Plan, complete with practical actions and learning resources.
                </CardDescription>
              </CardText>
            </ActionCard>
          </CardGrid>
        </CardSection>
      </SlideBanner>
    </SlideContainer>
  );
};

export default Slide1AssessmentComplete;
